<template>
  <ModalStep
    id="contact-warning-modal-content"
    data-test-id="title-contact-warning"
    :header="{
      title: t('chat.contactWarningModal.title'),
    }"
  >
    <template #bodyContent>
      <p>{{ t('chat.contactWarningModal.body.intro') }}</p>
      <ul>
        <li v-html="t('chat.contactWarningModal.body.benefit1')"></li>
        <li v-html="t('chat.contactWarningModal.body.benefit2')"></li>
      </ul>
      <p class="warning">{{ t('chat.contactWarningModal.body.warning') }}</p>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          :is-loading="isSendPendingRef"
          usage="button"
          type="submit"
          color="white"
          data-test-id="send-contact-warning"
          :text="t('chat.contactWarningModal.button.send')"
          @emit:click="sendMessageWithModerationHandler"
        />
        <CustomButton
          :is-loading="isCancelPendingRef"
          usage="button"
          type="submit"
          color="primary"
          data-test-id="cancel-contact-warning"
          :text="t('chat.contactWarningModal.button.cancel')"
          @emit:click="cancelMessageWithModerationHandler"
        />
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed } from 'vue'
import { toast } from 'vue-sonner'

import { cancelMessageWithModerationMutation, sendMessageWithModerationMutation } from '@/features/chat/api'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import { selectGetContactWarningModal } from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { useI18n } from '@/lib/i18n'
import { invalidateConversations } from '@/utils/lib/vue-query'

const { t } = useI18n()

type ContactWarningModalAttachedDataType = {
  messageId?: string
  conversationId?: string
}

const contactWarningModalRef = selectGetContactWarningModal(modalsStore) as {
  value: {
    attachedData?: ContactWarningModalAttachedDataType
  }
}
console.log('contactWarningModalRef', contactWarningModalRef.value)

const messageId = computed(() => contactWarningModalRef.value.attachedData?.messageId ?? null)
const conversationId = computed(() => contactWarningModalRef.value.attachedData?.conversationId ?? null)

console.log('message', messageId.value)

const { mutate: sendMessageWithModeration, isPending: isSendPendingRef } = useMutation({
  mutationFn: sendMessageWithModerationMutation,
  onSuccess: async () => {
    modalsStore.toggleModal('contact-warning', false)
    await invalidateConversations()
  },
  onError: (error) => {
    console.error(error)
    toast?.error(t('error.common'))
  },
})

const { mutate: cancelMessageWithModeration, isPending: isCancelPendingRef } = useMutation({
  mutationFn: cancelMessageWithModerationMutation,
  onSuccess: async () => {
    modalsStore.toggleModal('contact-warning', false)
    await invalidateConversations()
  },
  onError: (error) => {
    console.error(error)
    toast?.error(t('error.common'))
  },
})

function sendMessageWithModerationHandler() {
  if (messageId.value && conversationId.value) {
    sendMessageWithModeration({ messageId: messageId.value, conversationId: conversationId.value })
  } else {
    console.error('No message ID or conversation ID provided')
  }
}

function cancelMessageWithModerationHandler() {
  if (messageId.value) {
    cancelMessageWithModeration({ messageId: messageId.value })
  } else {
    console.error('No message ID provided')
  }
}
</script>

<style lang="scss">
@import '@/styles/utils';

.warning {
  background-color: rgb($badge-warning-bg);
  color: rgb($badge-warning-fg);
  padding: 24px;
  border-radius: 5px;
}
</style>
